import React, { useMemo, useState } from 'react'
import { BASE_URL } from '../helper/constants';
import LanguageContext from '../helper/LanguageContext';
import axios from 'axios';
import '../styles/components.css'
import './DownloadFileWithIcon.css'
import {
    formatDocSize,
    getDocExtension,
    getDocFileUrl,
    getDocIconName,
    getDocMessageName,
} from '../utils/documentAlbum';

// Type tile of the mobile Pro document card (proChatKit.docToneFor): PDF red, spreadsheet
// green, Word blue, slides amber, anything else the neutral chip.
export function docToneOf(iconName, extension) {
    const ext = String(extension || '').toLowerCase();
    if (iconName === 'table_chart' || /^(xls|xlsx|xlsm|csv|ods|numbers)$/.test(ext)) return { icon: 'table_chart', tone: 'ok' };
    if (iconName === 'picture_as_pdf' || ext === 'pdf') return { icon: 'picture_as_pdf', tone: 'err' };
    if (/^(doc|docx|odt|rtf|txt|pages)$/.test(ext)) return { icon: 'description', tone: 'info' };
    if (iconName === 'slideshow' || /^(ppt|pptx|key|odp)$/.test(ext)) return { icon: 'slideshow', tone: 'warn' };
    return { icon: iconName || 'draft', tone: 'neutral' };
}

/**
 * Document card (mobile ProMediaBlocks.ProDocCard): at least 240 wide, padding 8, radius 12,
 * 1px line; the 40×44 type tile, the name on one line, "PDF · 1.2 MB" and the action
 * (download · spinner with % · red retry). `overflow` > 0 shows "+n" instead of the action
 * (stacked album cards). Same props as before (info, group, isMe, onPreview).
 */
function DownloadFileWithIcon({ info, group, isMe, onPreview, overflow = 0 }) {
    const { strings, isGerm } = React.useContext(LanguageContext);
    const tr = (key, de, en) => (strings && strings[key]) || (isGerm ? de : en);
    const url = getDocFileUrl(info);
    const [progress, setProgress] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [done, setDone] = useState(false);
    const [failed, setFailed] = useState(false);

    const name = useMemo(() => getDocMessageName(info), [info]);
    const look = useMemo(() => docToneOf(getDocIconName(info), getDocExtension(info)), [info]);
    const canOpenPreview = typeof onPreview === 'function';
    const meta = useMemo(() => {
        const extension = getDocExtension(info).toUpperCase();
        const size = formatDocSize(info?.fileSize || info?.size || 0);
        return [extension, size].filter(Boolean).join(' · ');
    }, [info]);

    const _downloadFile = (fileUrl) => {
        setDownloading(true);
        setFailed(false);
        setProgress(0);
        axios({
            url: BASE_URL + fileUrl,
            method: "GET",
            responseType: "blob",
            onDownloadProgress: (progressEvent) => {
                if (!progressEvent.total) return;
                setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            },
        })
            .then(response => {
                const getFile = (filePath) => filePath?.split('/').pop();
                const getOutput = () => {
                    const fullUrl = BASE_URL + fileUrl;
                    const fileName = getFile(fullUrl);
                    const extension = fullUrl?.split('.').pop();
                    return info.attachedFileName
                        ? info.attachedFileName.replace(extension, '').replace('.', '') + "." + extension
                        : fileName + "." + extension;
                };
                const href = URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', getOutput());
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
                setDone(true);
                // The check mark is only an acknowledgement; the button returns to
                // the download state so the file can be fetched again.
                setTimeout(() => setDone(false), 2000);
            })
            .catch(err => {
                console.log(err);
                setFailed(true);
            })
            .finally(() => {
                setDownloading(false);
                setProgress(0);
            });
    };

    const handleDownloadClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!downloading && url) _downloadFile(url);
    };

    const handleCardClick = (e) => {
        e.stopPropagation();
        if (downloading) return;
        if (canOpenPreview) {
            onPreview(info);
            return;
        }
        if (url) _downloadFile(url);
    };

    const onActionKey = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleDownloadClick(e);
        }
    };

    const renderAction = () => {
        if (downloading) {
            return (
                <div className="kjc-doc__action is-busy" role="status" aria-label={`${progress}%`}>
                    <div className="kjc-doc__spinner" aria-hidden="true" />
                    {progress > 0 ? <div className="kjc-doc__pct">{progress}%</div> : null}
                </div>
            );
        }
        if (failed) {
            const label = tr('chat_doc_retry', 'Erneut versuchen', 'Retry');
            return (
                <div className="kjc-doc__action is-retry" role="button" tabIndex={0} title={label} aria-label={label} onClick={handleDownloadClick} onKeyDown={onActionKey}>
                    <div className="material-symbols-outlined kjc-doc__action-icon">refresh</div>
                </div>
            );
        }
        if (overflow > 0) {
            return <div className="kjc-doc__overflow">+{overflow}</div>;
        }
        const label = tr('chat_doc_download', 'Herunterladen', 'Download');
        return (
            <div className="kjc-doc__action" role="button" tabIndex={0} title={label} aria-label={label} onClick={handleDownloadClick} onKeyDown={onActionKey}>
                <div className="material-symbols-outlined kjc-doc__action-icon">{done ? 'check' : 'download'}</div>
            </div>
        );
    };

    // Divs rather than spans: the chat bubble forces its own font size and colour onto spans.
    return (
        <div
            className="kjc-doc"
            role="button"
            tabIndex={0}
            title={name}
            onClick={handleCardClick}
            onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(e);
                }
            }}
        >
            <div className={`kjc-doc__tile kjc-doc__tile--${look.tone}`}>
                <div className="material-symbols-outlined kjc-doc__glyph">{look.icon}</div>
            </div>
            <div className="kjc-doc__info">
                <div className="kjc-doc__name">{name}</div>
                {meta ? <div className="kjc-doc__meta">{meta}</div> : null}
            </div>
            {renderAction()}
        </div>
    );
}

export default DownloadFileWithIcon
